<script setup>
import { useStore } from 'vuex'
import PopUp from '~/components/PopUp.vue'

const { $auth, $global, $router } = useNuxtApp() // Access $auth and $global
const config = useRuntimeConfig()
const version = computed(() => config.public.APP_VERSION)
const getUserInfo = computed(() => store.getters['users/getUserInfo'])
const getWasteCompanyBranchOptions = computed(() => store.getters['users/getBranches'])
const store = useStore()
const getBranch = id => store.getters['users/getBranch'](id)

const showSelf = defineModel()
const wasteCompanyBranchId = ref(null)
function authUserName(fullName = false) {
  // for 'social' auth method
  if ($auth.user && $auth.user.name) return $auth.user.name
  // for 'oidc' auth method
  let userInfo = getUserInfo.value
  if (userInfo) return fullName ? userInfo.fullName : userInfo.lastName || userInfo.firstName
}

function transformWasteCompanyBranchOption(option) {
  return {
    value: option.wasteCompanyBranchId,
    label: `${option.wasteCompanyName} - ${option.wasteCompanyBranchName}`,
    logoUrl: option.wasteCompanyLogoUrl ? $global.prefixImageUrl(option.wasteCompanyLogoUrl) : null,
  }
}
function chooseWasteCompanyBranch() {
  if (!wasteCompanyBranchId.value) {
    return
  }
  // SET wasteCompanyBranchId to VUEX global && use it
  // This should be possible only for "admin" & "spravce" - all of it.
  let culture = getBranch(wasteCompanyBranchId.value)?.wasteCompanyCulture ?? 'cs'
  if (culture === '') culture = 'cs'
  store.dispatch('global/setCountryCulture', culture)

  let countryCode = null
  if (culture === 'cs') countryCode = 'CZ'
  else if (culture === 'sk') countryCode = 'SK'

  if (countryCode) {
    store.dispatch('global/setCountryCode', countryCode)
  }

  store.dispatch('global/setWasteCompanyBranchId', wasteCompanyBranchId.value)
  // fetch logo of the chosen branch
  store.dispatch('waste_companies/fetchLogo', getBranch(wasteCompanyBranchId.value)?.wasteCompanyId)
  $global.getTranslator().setLocale(culture)
}
async function redirectToUserProfile() {
  showSelf.value = false
  await $router.push('/profile')
}
async function logout() {
  await $auth.logout()
  $router.push('/login')
}
const wasteCompanyBranchRef = ref(null)
</script>
<template>
  <PopUp v-model="showSelf" class="popUp" exit-button>
    <div class="content">
      <h2>
        {{ authUserName(true) }}
      </h2>
      <div class="actionButton">
        <div class="companySelector">
          <label for="formNewUserRole" class="form-label">
            {{ $_('components/navbar.modal.waste_company_selection', 'Výběr firmy') }}
          </label>
          <search-select
            id="formNewUserRole"
            v-model="wasteCompanyBranchId"
            ref="wasteCompanyBranchRef"
            :config="{
              fetch: {
                url: 'users/fetchAllCompanyBranches',
              },
              get: {
                url: 'users/getBranches',
              },
            }"
            :options="getWasteCompanyBranchOptions"
            :transform="transformWasteCompanyBranchOption"
            :aria-label="$_('components/navbar.modal.waste_company_selection', 'Výběr firmy')"
            :select-first="false"
          >
            <template #option="option">
              <CompanyInfo
                :src="option.logoUrl"
                :alt="option.label"
                :text="option.label"
                classNames="whitespace-normal"
              />
            </template>
            <template #selected-option="option">
              <CompanyInfo :src="option.logoUrl" :alt="option.label" :text="option.label" />
            </template>
          </search-select>
        </div>
        <button
          type="button"
          class="btn btn-primary w-100"
          @click="chooseWasteCompanyBranch()"
          :disabled="!wasteCompanyBranchId"
        >
          {{ $_('components/navbar.modal.buttons.select_waste_company', 'Vybrat firmu') }}
        </button>
        <div class="rowItems">
          <button type="button" class="btn btn-outline-primary" @click="redirectToUserProfile()">
            {{ $_('components/navbar.modal.buttons.user_profile', 'Profil uživatele') }}
          </button>
          <button dusk="logout-button" type="button" class="btn btn-outline-primary" @click="logout()">
            {{ $_('components/navbar.modal.buttons.logout', 'Odhlásit') }}
          </button>
        </div>
      </div>
      <div class="text-muted text-end">
        {{ `${$_('components/navbar.modal.version', 'verze')} ${version}` }}
      </div>
    </div>
  </PopUp>
</template>
<style scoped>
.content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.actionButton {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
h2 {
  margin: 0;
  max-width: 430px;
}
.companySelector {
  width: 430px;
  max-width: 100%;
}
.rowItems {
  display: flex;
  flex-wrap: wrap;
  text-wrap: nowrap;
  white-space: nowrap;
  justify-content: space-between;
  gap: 12px;
}
.rowItems > * {
  flex: 1 1 0px;
}
</style>
